import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

const PRIORITIES = [
  { value: null, label: 'No priority', shortcut: '0' },
  { value: 'critical', label: 'Urgent', shortcut: '1' },
  { value: 'high', label: 'High', shortcut: '2' },
  { value: 'medium', label: 'Medium', shortcut: '3' },
  { value: 'low', label: 'Low', shortcut: '4' },
]

// Priority icon components
function UrgentIcon({ className = '' }) {
  return (
    <span className={`inline-flex items-center justify-center w-4 h-4 bg-red-500 rounded text-white font-bold text-xs ${className}`}>
      !
    </span>
  )
}

function BarsIcon({ filled = 1, color = 'text-slate-400', className = '' }) {
  return (
    <svg className={`w-4 h-4 ${color} ${className}`} viewBox="0 0 16 16" fill="currentColor">
      <rect x="1" y="8" width="3" height="6" rx="0.5" opacity={filled >= 1 ? 1 : 0.3} />
      <rect x="6" y="5" width="3" height="9" rx="0.5" opacity={filled >= 2 ? 1 : 0.3} />
      <rect x="11" y="2" width="3" height="12" rx="0.5" opacity={filled >= 3 ? 1 : 0.3} />
    </svg>
  )
}

function NoPriorityIcon({ className = '' }) {
  return (
    <span className={`inline-flex items-center justify-center w-4 h-4 text-slate-500 font-bold text-xs tracking-tight ${className}`}>
      •••
    </span>
  )
}

function getPriorityIcon(priority, isHovered = false) {
  const hoverClass = isHovered ? 'opacity-80' : ''

  switch (priority) {
    case 'critical':
      return <UrgentIcon className={hoverClass} />
    case 'high':
      return <BarsIcon filled={3} color="text-orange-500" className={hoverClass} />
    case 'medium':
      return <BarsIcon filled={2} color="text-yellow-500" className={hoverClass} />
    case 'low':
      return <BarsIcon filled={1} color="text-slate-400" className={hoverClass} />
    default:
      return <NoPriorityIcon className={hoverClass} />
  }
}

function getPriorityLabel(priority) {
  const found = PRIORITIES.find((p) => p.value === priority)
  return 'Customer Priority: ' + (found?.label || 'No priority')
}

function PriorityIconDropdown({ value, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)
  const tooltipTimeout = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      })
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (tooltipTimeout.current) {
        clearTimeout(tooltipTimeout.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    setIsHovered(true)
    tooltipTimeout.current = setTimeout(() => {
      if (!isOpen) {
        setShowTooltip(true)
      }
    }, 500)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    setShowTooltip(false)
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current)
    }
  }

  const handleToggle = (e) => {
    e.stopPropagation()
    e.preventDefault()
    if (!disabled) {
      setIsOpen(!isOpen)
      setShowTooltip(false)
    }
  }

  const handleSelect = (e, priority) => {
    e.stopPropagation()
    e.preventDefault()
    if (onChange && priority.value !== value) {
      onChange(priority.value)
    }
    setIsOpen(false)
    setShowTooltip(false)
  }

  return (
    <div
      className="relative"
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          inline-flex items-center justify-center w-6 h-6 rounded
          transition-colors duration-150
          ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-xano-cardHover'}
        `}
      >
        {getPriorityIcon(value, isHovered)}
      </button>

      {/* Tooltip */}
      {showTooltip && !isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded shadow-lg whitespace-nowrap z-50">
          {getPriorityLabel(value)}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
        </div>
      )}

      {/* Dropdown - rendered via portal to escape stacking context */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="w-48 rounded-lg shadow-2xl overflow-hidden border border-xano-border"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 99999,
            backgroundColor: '#1c2128',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-3 py-2 border-b border-xano-border"
            style={{ backgroundColor: '#1c2128' }}
          >
            <span className="text-xs text-xano-textMuted">Customer priority</span>
            <span className="text-xs text-xano-textMuted px-1.5 py-0.5 rounded" style={{ backgroundColor: '#2d333b' }}>P</span>
          </div>

          {/* Options */}
          <div className="py-1" style={{ backgroundColor: '#1c2128' }}>
            {PRIORITIES.map((priority) => (
              <button
                key={priority.value || 'none'}
                type="button"
                onClick={(e) => handleSelect(e, priority)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm text-left
                  transition
                  ${priority.value === value ? 'text-xano-text' : 'text-xano-textMuted'}
                `}
                style={{ backgroundColor: '#1c2128' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d333b'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1c2128'}
              >
                <span className="w-4 flex justify-center">
                  {getPriorityIcon(priority.value)}
                </span>
                <span className="flex-1">{priority.label}</span>
                {priority.value === value && (
                  <svg
                    className="w-4 h-4 text-xano-text"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
                <span className="text-xs text-xano-textMuted w-4 text-right">{priority.shortcut}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default PriorityIconDropdown
